import type {
  gasEstimateForShieldBaseToken,
  populateShieldBaseToken,
  gasEstimateForShield,
  populateShield
} from '@railgun-community/wallet'
import { loadRailgunSdk } from '@web/utils/railgunSdk'
import {
  TXIDVersion,
  type RailgunERC20AmountRecipient,
  NETWORK_CONFIG
} from '@railgun-community/shared-models'
import { Contract, JsonRpcProvider } from 'ethers'

import { getWrappedBaseTokenAddress } from './railgunTokens'
import { getNetworkNameForChainId, getRpcUrlForChainId } from './railgunCore'
import { derivePublicSigner } from './railgunMnemonic'
import {
  getShieldSignature,
  getErrorMessage,
  ensureERC20Allowance,
  readERC20Allowance,
  getGasDetailsForTransaction,
  computeGasFeeNativeAndUsd,
  computeGasFeeOptionsNativeAndUsd,
  isNoteAlreadySpentError,
  type RailgunGasFeeOption,
  type RailgunGasSpeedName
} from './railgunShared'

// Gas-unit fallbacks used ONLY to preview a fee when the token has no allowance yet
// (see estimateShieldGasERC20). Deliberately padded so the estimate never underquotes;
// the exact gas is resolved on-chain at execute time once the approve has landed.
const ERC20_APPROVE_GAS = 60_000n
const SHIELD_GAS_ESTIMATE_FALLBACK = 400_000n
import { FEATURES } from '@web/constants/features'
import { translation } from '@web/utils/safeTranslation'
import { sdkInvokeInOffscreen } from '@web/extension-services/background/offscreen/railgunOffscreenClient'

// Engine in-process (flag off) vs offscreen (flag on) — same routing pattern as railgunTransfer.

async function invokeGasEstimateForShield(
  args: Parameters<typeof gasEstimateForShield>
): Promise<Awaited<ReturnType<typeof gasEstimateForShield>>> {
  if (FEATURES.railgunEngineOffscreenEnabled) {
    return sdkInvokeInOffscreen({ method: 'gasEstimateForShield', args })
  }
  const { gasEstimateForShield } = await loadRailgunSdk()
  return gasEstimateForShield(...args)
}

async function invokePopulateShield(
  args: Parameters<typeof populateShield>
): Promise<Awaited<ReturnType<typeof populateShield>>> {
  if (FEATURES.railgunEngineOffscreenEnabled) {
    return sdkInvokeInOffscreen({ method: 'populateShield', args })
  }
  const { populateShield } = await loadRailgunSdk()
  return populateShield(...args)
}

async function invokeGasEstimateForShieldBaseToken(
  args: Parameters<typeof gasEstimateForShieldBaseToken>
): Promise<Awaited<ReturnType<typeof gasEstimateForShieldBaseToken>>> {
  if (FEATURES.railgunEngineOffscreenEnabled) {
    return sdkInvokeInOffscreen({ method: 'gasEstimateForShieldBaseToken', args })
  }
  const { gasEstimateForShieldBaseToken } = await loadRailgunSdk()
  return gasEstimateForShieldBaseToken(...args)
}

async function invokePopulateShieldBaseToken(
  args: Parameters<typeof populateShieldBaseToken>
): Promise<Awaited<ReturnType<typeof populateShieldBaseToken>>> {
  if (FEATURES.railgunEngineOffscreenEnabled) {
    return sdkInvokeInOffscreen({ method: 'populateShieldBaseToken', args })
  }
  const { populateShieldBaseToken } = await loadRailgunSdk()
  return populateShieldBaseToken(...args)
}

export async function shieldBaseTokenWithSDK(params: {
  mnemonic: string
  passphrase?: string
  chainId: number
  amount: bigint
  railgunWalletAddress: string
  feeSpeed?: RailgunGasSpeedName
}): Promise<string> {
  try {
    const { mnemonic, chainId, amount, railgunWalletAddress, feeSpeed } = params

    const networkName = getNetworkNameForChainId(chainId)
    if (!networkName) {
      throw new Error(`Unsupported chainId for Railgun shield: ${chainId}`)
    }

    const rpcUrl = getRpcUrlForChainId(chainId)
    if (!rpcUrl) {
      throw new Error(`No RPC URL configured for chainId ${chainId}`)
    }

    const wrappedTokenAddress = getWrappedBaseTokenAddress(chainId)
    if (!wrappedTokenAddress) {
      throw new Error(`No wrapped base token configured for chainId ${chainId}`)
    }

    const provider = new JsonRpcProvider(rpcUrl)
    const wallet = derivePublicSigner(mnemonic, params.passphrase, provider)

    const shieldPrivateKey = await getShieldSignature(wallet)
    const fromWalletAddress = wallet.address

    const erc20AmountRecipient: RailgunERC20AmountRecipient = {
      tokenAddress: wrappedTokenAddress,
      amount,
      recipientAddress: railgunWalletAddress
    }

    const { gasEstimate } = await invokeGasEstimateForShieldBaseToken([
      TXIDVersion.V2_PoseidonMerkle,
      networkName,
      railgunWalletAddress,
      shieldPrivateKey,
      erc20AmountRecipient,
      fromWalletAddress
    ])

    const gasDetails = await getGasDetailsForTransaction(
      networkName,
      gasEstimate,
      true,
      wallet,
      chainId,
      feeSpeed
    )

    const { transaction } = await invokePopulateShieldBaseToken([
      TXIDVersion.V2_PoseidonMerkle,
      networkName,
      railgunWalletAddress,
      shieldPrivateKey,
      erc20AmountRecipient,
      gasDetails
    ])

    const tx = await wallet.sendTransaction(transaction)
    await tx.wait()
    return tx.hash
  } catch (err) {
    console.error('shieldBaseTokenWithSDK failed:', err)
    wrapShieldError(err)
  }
}

function wrapShieldError(err: unknown): never {
  const message = getErrorMessage('shield', err)
  throw err instanceof Error && message === err.message ? err : new Error(message, { cause: err })
}

export type ShieldGasEstimation = {
  gasFeeAmountNative: string
  gasFeeUsd?: string
  feeOptions?: RailgunGasFeeOption[]
}

export async function estimateShieldGasBaseToken(params: {
  mnemonic: string
  passphrase?: string
  chainId: number
  amount: bigint
  railgunWalletAddress: string
}): Promise<ShieldGasEstimation> {
  const { mnemonic, chainId, amount, railgunWalletAddress } = params

  const networkName = getNetworkNameForChainId(chainId)
  if (!networkName) {
    throw new Error(`Unsupported chainId for Railgun shield: ${chainId}`)
  }

  const rpcUrl = getRpcUrlForChainId(chainId)
  if (!rpcUrl) {
    throw new Error(`No RPC URL configured for chainId ${chainId}`)
  }

  const provider = new JsonRpcProvider(rpcUrl)
  const wallet = derivePublicSigner(mnemonic, params.passphrase, provider)

  const shieldPrivateKey = await getShieldSignature(wallet)
  const fromWalletAddress = wallet.address

  const wrappedTokenAddress = getWrappedBaseTokenAddress(chainId)
  if (!wrappedTokenAddress) {
    throw new Error(`No wrapped base token configured for chainId ${chainId}`)
  }

  const erc20AmountRecipient: RailgunERC20AmountRecipient = {
    tokenAddress: wrappedTokenAddress,
    amount,
    recipientAddress: railgunWalletAddress
  }

  const { gasEstimate } = await invokeGasEstimateForShieldBaseToken([
    TXIDVersion.V2_PoseidonMerkle,
    networkName,
    railgunWalletAddress,
    shieldPrivateKey,
    erc20AmountRecipient,
    fromWalletAddress
  ])

  const gasDetails = await getGasDetailsForTransaction(
    networkName,
    gasEstimate,
    true,
    wallet,
    chainId
  )

  const feeSummary = await computeGasFeeNativeAndUsd({ gasEstimate, gasDetails, chainId })
  const feeOptions = await computeGasFeeOptionsNativeAndUsd({ gasEstimate, chainId, wallet })

  return {
    ...feeSummary,
    feeOptions
  }
}

export async function estimateShieldGasERC20(params: {
  mnemonic: string
  passphrase?: string
  chainId: number
  amount: bigint
  tokenAddress: string
  railgunWalletAddress: string
}): Promise<ShieldGasEstimation> {
  const { mnemonic, chainId, amount, tokenAddress, railgunWalletAddress } = params

  const networkName = getNetworkNameForChainId(chainId)
  if (!networkName) {
    throw new Error(`Unsupported chainId for Railgun shield: ${chainId}`)
  }

  const rpcUrl = getRpcUrlForChainId(chainId)
  if (!rpcUrl) {
    throw new Error(`No RPC URL configured for chainId ${chainId}`)
  }

  const provider = new JsonRpcProvider(rpcUrl)
  const wallet = derivePublicSigner(mnemonic, params.passphrase, provider)

  const shieldPrivateKey = await getShieldSignature(wallet)
  const fromWalletAddress = wallet.address

  // The RAILGUN proxy pulls the ERC20 via transferFrom, so the SDK's on-chain
  // gasEstimate reverts unless the proxy already has allowance.
  const network = NETWORK_CONFIG[networkName]
  const spender = network?.proxyContract
  if (!spender) {
    throw new Error(`No proxy contract for network: ${networkName}`)
  }

  // Gas estimation MUST be read-only. Do NOT broadcast an approve here just to
  // preview a fee — that spends real gas, blocks a full block on `.wait()`, and
  // fails outright when the public EOA holds no native token (surfacing as the
  // generic "Unable to estimate gas"). The real approve is sent at execute time in
  // shieldERC20WithSDK, which already calls ensureERC20Allowance.
  const tokenContract = new Contract(tokenAddress, ERC20_APPROVE_ABI, wallet)
  const allowance = await readERC20Allowance({
    tokenContract: tokenContract as unknown as {
      allowance: (o: string, s: string) => Promise<bigint>
    },
    owner: fromWalletAddress,
    spender
  })

  const erc20AmountRecipients: RailgunERC20AmountRecipient[] = [
    {
      tokenAddress,
      amount,
      recipientAddress: railgunWalletAddress
    }
  ]

  let gasEstimate: bigint
  if (allowance >= amount) {
    // Allowance already covers the shield → the SDK's on-chain estimate succeeds
    // and gives an accurate gas figure.
    ;({ gasEstimate } = await invokeGasEstimateForShield([
      TXIDVersion.V2_PoseidonMerkle,
      networkName,
      shieldPrivateKey,
      erc20AmountRecipients,
      [],
      fromWalletAddress
    ]))
  } else {
    // No allowance yet: the SDK estimate would revert on transferFrom, so approximate
    // the fee as one ERC20 approve + a shield. The exact gas is resolved at execute
    // time (after the approve lands) — this only keeps the fee/speed options visible
    // without mutating chain state.
    gasEstimate = SHIELD_GAS_ESTIMATE_FALLBACK + ERC20_APPROVE_GAS
  }

  const gasDetails = await getGasDetailsForTransaction(
    networkName,
    gasEstimate,
    true,
    wallet,
    chainId
  )

  const feeSummary = await computeGasFeeNativeAndUsd({ gasEstimate, gasDetails, chainId })
  const feeOptions = await computeGasFeeOptionsNativeAndUsd({ gasEstimate, chainId, wallet })

  return {
    ...feeSummary,
    feeOptions
  }
}

const ERC20_APPROVE_ABI = [
  'function allowance(address owner, address spender) view returns (uint256)',
  'function approve(address spender, uint256 amount) external returns (bool)'
] as const

/**
 * Maps raw gas estimate errors to short, user-friendly messages for the shield summary.
 */
export function getGasEstimateErrorMessage(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err)
  const full = msg.toLowerCase()

  // A note the wallet still lists as spendable was already nullified on-chain
  // (local spent-state lagging, or a shared-note passphrase-collision). A light
  // balance refresh (triggered by the caller) re-scans the nullifier and drops the
  // note, so the right guidance is "syncing, retry" — not "token unsupported".
  // Checked before the generic revert branch, which would otherwise swallow it.
  if (isNoteAlreadySpentError(err)) {
    return translation.t(
      'Your private balance is syncing after a recent transaction. Please try again in a few seconds.'
    )
  }

  if (
    full.includes('insufficient') ||
    full.includes('exceeds balance') ||
    full.includes('transfer amount exceeds') ||
    full.includes('balance too low') ||
    full.includes('amount exceeds')
  ) {
    return translation.t('Insufficient amount. Check your balance and try a smaller amount.')
  }

  if (
    full.includes('allowance') ||
    (full.includes('approve') && !full.includes('approval completed'))
  ) {
    return translation.t('Token approval failed. You may need to approve this token first.')
  }

  if (
    full.includes('missing revert data') ||
    full.includes('revert data') ||
    full.includes('call_exception') ||
    full.includes('execution reverted') ||
    full.includes('transaction would revert') ||
    full.includes('simulation failed')
  ) {
    return translation.t(
      'Transaction would fail. Token may not be supported on this network, or check balance and approval.'
    )
  }

  if (
    full.includes('bad_data') ||
    full.includes('could not decode') ||
    full.includes('token not supported') ||
    full.includes('unsupported token')
  ) {
    return translation.t('This token is not supported for shielding on this network.')
  }

  if (
    full.includes('unsupported chainid') ||
    full.includes('no rpc url') ||
    full.includes('no proxy contract') ||
    full.includes('rpc connection') ||
    full.includes('network error') ||
    full.includes('timeout') ||
    full.includes('econnrefused')
  ) {
    return translation.t('Network error. Check your connection or try again later.')
  }

  return translation.t('Unable to estimate gas. Please try again or use another token.')
}

export async function shieldERC20WithSDK(params: {
  mnemonic: string
  passphrase?: string
  chainId: number
  amount: bigint
  tokenAddress: string
  railgunWalletAddress: string
  feeSpeed?: RailgunGasSpeedName
}): Promise<string> {
  try {
    const { mnemonic, chainId, amount, tokenAddress, railgunWalletAddress, feeSpeed } = params

    const networkName = getNetworkNameForChainId(chainId)
    if (!networkName) {
      throw new Error(`Unsupported chainId for Railgun shield: ${chainId}`)
    }

    const rpcUrl = getRpcUrlForChainId(chainId)
    if (!rpcUrl) {
      throw new Error(`No RPC URL configured for chainId ${chainId}`)
    }

    const network = NETWORK_CONFIG[networkName]
    const spender = network?.proxyContract
    if (!spender) {
      throw new Error(`No proxy contract for network: ${networkName}`)
    }

    const provider = new JsonRpcProvider(rpcUrl)
    const wallet = derivePublicSigner(mnemonic, params.passphrase, provider)

    const shieldPrivateKey = await getShieldSignature(wallet)
    const fromWalletAddress = wallet.address

    const tokenContract = new Contract(tokenAddress, ERC20_APPROVE_ABI, wallet)
    await ensureERC20Allowance({
      tokenContract: tokenContract as unknown as {
        allowance: (o: string, s: string) => Promise<bigint>
        approve: (s: string, a: bigint) => Promise<{ wait: () => Promise<unknown> }>
      },
      owner: fromWalletAddress,
      spender,
      amount
    })

    const erc20AmountRecipients: RailgunERC20AmountRecipient[] = [
      {
        tokenAddress,
        amount,
        recipientAddress: railgunWalletAddress
      }
    ]

    const { gasEstimate } = await invokeGasEstimateForShield([
      TXIDVersion.V2_PoseidonMerkle,
      networkName,
      shieldPrivateKey,
      erc20AmountRecipients,
      [],
      fromWalletAddress
    ])

    const gasDetails = await getGasDetailsForTransaction(
      networkName,
      gasEstimate,
      true,
      wallet,
      chainId,
      feeSpeed
    )

    const { transaction } = await invokePopulateShield([
      TXIDVersion.V2_PoseidonMerkle,
      networkName,
      shieldPrivateKey,
      erc20AmountRecipients,
      [],
      gasDetails
    ])

    const tx = await wallet.sendTransaction(transaction)
    await tx.wait()
    return tx.hash
  } catch (err) {
    console.error('shieldERC20WithSDK failed:', err)
    wrapShieldError(err)
  }
}
